import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client.ts';
import { Alert, Button, ConfirmDialog, LoadingBlock, Select, TextArea } from './ui.tsx';
import { useSwapTime } from './shift-swaps.tsx';
import type { SwapCandidate } from '@shared/shift-swaps.ts';

interface Candidates {
  items: SwapCandidate[];
  closesAt: string;
  closed: boolean;
  cutoffHours: number;
  /** Would "anyone available" reach somebody? Missing from older servers. */
  anyoneAvailable?: boolean;
}

const ANYONE = '';

/**
 * My jobs › visit › "Ask for swap": pick a coworker who is free then (or
 * anyone available), give the visit away or trade it for one of theirs, add a
 * note. The coworker answers first; the office approves before any crew moves.
 */
export function AskForSwap({ appointmentId, timezone }: { appointmentId: string; timezone: string }) {
  const { t } = useTranslation();
  const time = useSwapTime();
  const [open, setOpen] = useState(false);
  // Hidden while the company has swaps switched off; existing swaps stay on Swaps.
  const [allowed, setAllowed] = useState<boolean | null>(null);
  useEffect(() => {
    let alive = true;
    api
      .get<{ swapRequests?: boolean }>('/staff/capability')
      .then((cap) => alive && setAllowed(cap.swapRequests !== false))
      .catch(() => alive && setAllowed(false));
    return () => {
      alive = false;
    };
  }, []);
  const [candidates, setCandidates] = useState<Candidates | null>(null);
  const [loadError, setLoadError] = useState('');
  const [target, setTarget] = useState(ANYONE);
  const [trade, setTrade] = useState(false);
  const [offered, setOffered] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const mutation = useRef<string | null>(null);

  async function start() {
    setOpen(true);
    setSent(false);
    setError('');
    setLoadError('');
    setCandidates(null);
    setTarget(ANYONE);
    setTrade(false);
    setOffered('');
    setNote('');
    mutation.current = null;
    try {
      setCandidates(
        await api.get<Candidates>(`/staff/swaps/candidates?appointmentId=${encodeURIComponent(appointmentId)}`),
      );
    } catch (caught) {
      const status = (caught as { status?: number }).status;
      setLoadError(
        status === 409
          ? t('shiftSwaps.off')
          : status && status < 500
            ? t('shiftSwaps.unavailable')
            : t('shiftSwaps.readFailed'),
      );
    }
  }

  const person = candidates?.items.find((row) => row.id === target);
  const tradeVisits = person?.visits ?? [];
  // A trade closes at the cutoff before the earlier of the two visits.
  const closesAt = (trade && tradeVisits.find((visit) => visit.id === offered)?.closesAt) || candidates?.closesAt;
  // "Anyone available" goes only to coworkers who could take it as a give-away;
  // with none, the server refuses it. A trade-only coworker needs a trade.
  const ready =
    !!candidates &&
    !candidates.closed &&
    (person
      ? !person.tradeOnly || trade
      : (candidates.anyoneAvailable ?? candidates.items.some((row) => !row.tradeOnly))) &&
    (!trade || !!offered);

  async function submit() {
    if (!ready || busy) return;
    setBusy(true);
    setError('');
    // One retry identifier per attempt, kept until it is answered.
    mutation.current ??= crypto.randomUUID();
    try {
      await api.post('/staff/swaps', {
        clientMutationId: mutation.current,
        appointmentId,
        targetUserId: target || null,
        offeredAppointmentId: trade && offered ? offered : null,
        note,
      });
      mutation.current = null;
      setSent(true);
      setOpen(false);
    } catch (caught) {
      const status = (caught as { status?: number }).status;
      if (status && status < 500) mutation.current = null;
      setError(t('shiftSwaps.ask.failed'));
    } finally {
      setBusy(false);
    }
  }

  if (!allowed) return null;
  return (
    <div className="mt-4 space-y-2">
      <Button onClick={() => void start()}>{t('shiftSwaps.ask.button')}</Button>
      {sent ? (
        <Alert tone="success">
          {t('shiftSwaps.ask.sent')}{' '}
          <Link className="underline" to="/staff/swaps">
            {t('shiftSwaps.title')}
          </Link>
        </Alert>
      ) : null}
      {open ? (
        <ConfirmDialog
          title={t('shiftSwaps.ask.title')}
          confirmLabel={t('shiftSwaps.ask.submit')}
          confirmDisabled={!ready}
          busy={busy}
          error={error || null}
          onConfirm={() => void submit()}
          onCancel={() => setOpen(false)}
        >
          {loadError ? (
            <Alert tone="error">{loadError}</Alert>
          ) : !candidates ? (
            <LoadingBlock label={t('shiftSwaps.ask.loading')} />
          ) : candidates.closed ? (
            <Alert tone="info">{t('shiftSwaps.ask.closed', { hours: candidates.cutoffHours })}</Alert>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-[color:var(--color-muted)]">
                {t('shiftSwaps.closes', { time: time(closesAt ?? candidates.closesAt, timezone) })}
              </p>
              {!candidates.items.length ? <Alert tone="info">{t('shiftSwaps.ask.noCandidates')}</Alert> : null}
              <Select
                label={t('shiftSwaps.ask.who')}
                value={target}
                onChange={(event) => {
                  setTarget(event.target.value);
                  setOffered('');
                  const chosen = candidates.items.find((row) => row.id === event.target.value);
                  setTrade(!!chosen?.tradeOnly);
                }}
                options={[
                  { value: ANYONE, label: t('shiftSwaps.anyone') },
                  ...candidates.items.map((row) => ({ value: row.id, label: row.name })),
                ]}
              />
              <fieldset className="space-y-2">
                <legend className="font-semibold">{t('shiftSwaps.ask.mode')}</legend>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="swap-mode"
                    checked={!trade}
                    disabled={!!person?.tradeOnly}
                    onChange={() => setTrade(false)}
                  />
                  {t('shiftSwaps.ask.giveAway')}
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="swap-mode"
                    checked={trade}
                    disabled={target === ANYONE}
                    onChange={() => setTrade(true)}
                  />
                  {t('shiftSwaps.ask.trade')}
                </label>
              </fieldset>
              {trade ? (
                tradeVisits.length ? (
                  <Select
                    label={t('shiftSwaps.ask.tradeVisit')}
                    value={offered}
                    onChange={(event) => setOffered(event.target.value)}
                    options={[
                      { value: '', label: '—' },
                      ...tradeVisits.map((visit) => ({
                        value: visit.id,
                        label: `${visit.clientName} · ${time(visit.startsAt, visit.timezone)}${visit.area ? ` · ${visit.area}` : ''}`,
                      })),
                    ]}
                  />
                ) : (
                  <Alert tone="info">{t('shiftSwaps.ask.noTradeVisits')}</Alert>
                )
              ) : null}
              <TextArea
                label={t('shiftSwaps.ask.note')}
                maxLength={1000}
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
              <p className="text-sm text-[color:var(--color-muted)]">{t('shiftSwaps.ask.approval')}</p>
            </div>
          )}
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
